import { adminTenantId } from '@logto/schemas';
import type { ReactElement } from 'react';
import { useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import ErrorMessage from '@/shared/components/ErrorMessage';

import RegistrationAgreementPage from './RegistrationAgreementPage';
import RegistrationConsentContext from './RegistrationConsentContext';
import { getRegistrationLegalState } from './session';
import useRegistrationConsent from './use-registration-consent';

const RegistrationGate = ({ children }: { readonly children: ReactElement }) => {
  const { pathname } = useLocation();
  const { experienceSettings } = useContext(PageContext);
  const isAdminTenant = experienceSettings?.tenantId === adminTenantId;
  const [ready, setReady] = useState(() => Boolean(getRegistrationLegalState().reviewed));
  const [reviewRevision, setReviewRevision] = useState(0);
  const reviewAgain = useCallback(() => {
    setReady(false);
    setReviewRevision((revision) => revision + 1);
  }, []);
  const { identifierInputValue } = useContext(UserInteractionContext);
  const { consentError, saveRegistrationConsent } = useRegistrationConsent(reviewAgain);
  const continueRegistration = async () => {
    // A reloaded OTP screen already has the native identifier in context.
    if (
      pathname.endsWith('/verification-code') &&
      identifierInputValue?.type &&
      !(await saveRegistrationConsent(identifierInputValue.type, identifierInputValue.value))
    ) {
      return;
    }
    setReady(true);
  };
  const registration = /^\/(register|identifier-register)(?:\/|$)/.test(pathname);
  return (
    <RegistrationConsentContext.Provider value={reviewAgain}>
      {registration && !isAdminTenant && !ready ? (
        <>
          <RegistrationAgreementPage key={reviewRevision} onContinue={continueRegistration} />
          {consentError && <ErrorMessage>{consentError}</ErrorMessage>}
        </>
      ) : (
        children
      )}
    </RegistrationConsentContext.Provider>
  );
};
export default RegistrationGate;
